import {CommandLineInterface, CoreDI, inject, injectable, LanguageService, IEventManagerWithAsync} from '@gongxigames/framework/core';
import {IGlobalEventManger, LogicEvents, ErrorMessage as ServerErrorMessage, Message} from '@gongxigames/framework/slot';
import {PPSettings} from '@gongxigames/framework/integrations/pariplay/PPSettings';
import {ErrorTypes} from '@gongxigames/framework/integrations/pariplay/ErrorTypes';

const UniqueErrorCodes = [23, 24, 25];
const CONNECTION_ERROR_CODE = 4;

@injectable()
export class PPErrorsHandler {
    public static readonly INVALID_BALANCE_POPUP_ID: string = 'INVALID_BALANCE';
    public static readonly SLOW_CONNECTION_POPUP_ID: string = 'SLOW_CONNECTION';

    @inject(CoreDI.LanguageService)
    protected _language!: LanguageService;

    @inject(CoreDI.Settings)
    protected _settings!: PPSettings;

    protected _skipFollowingErrors = false;
    protected _eventManager: IGlobalEventManger;

    constructor(
        @inject(CoreDI.IEventManagerWithAsync) eventManager: IGlobalEventManger,
        @inject(CoreDI.CLI) private _cli: CommandLineInterface
    ) {
        this._eventManager = eventManager;

        /* develblock:start */
        this._cli.addCommand('PPErrorsHandler', {
            connectionTimeout: () => this.onConnectionTimeout(),
            connectionSlow: () => this.onConnectionSlow(),
            gameUnavailable: () => this.onGameUnavailable(),
            placeBetError: () => this.onPlaceBetError(),
            invalidBalanceError: () => this.onInvalidBalanceError()
        });
        /* develblock:end */

        this.init();
    }

    protected init(): void {
        this.on(LogicEvents.SPIN_RESPONSE, this.onResponse);
        this.on(LogicEvents.FREE_SPIN_RESPONSE, this.onResponse);
        this.on(LogicEvents.RESPIN_RESPONSE, this.onResponse);
        this.on(LogicEvents.PICK_RESPONSE, this.onResponse);
        this.on(LogicEvents.SELECT_RESPONSE, this.onResponse);
        this.on(LogicEvents.INIT_RESPONSE, this.onResponse.bind(this, false));

        this.on(LogicEvents.CONNECTION_TIMEOUT, this.onConnectionTimeout);
        this.on(LogicEvents.CONNECTION_SLOW, this.onConnectionSlow);
        this.on(LogicEvents.CONNECTION_ERROR, this.onConnectionError);
        this.on(LogicEvents.GAME_UNAVAILABLE, this.onGameUnavailable);
        this.on(LogicEvents.PLACE_BET_ERROR, this.onPlaceBetError);
        this.on(LogicEvents.TEMPORARY_ERROR, this.onTemporaryError);
        this.on(LogicEvents.GAMESERVER_ERROR, this.onGameServerError);
        this.on(LogicEvents.INVALID_BALANCE, this.onInvalidBalanceError);
        this.on(LogicEvents.BALANCE_UPDATED, this.onBalanceUpdated);
    }

    protected onResponse(shouldRemovePopup = true): void {
        if (shouldRemovePopup) {
            this.emitCloseCurrentPopup();
        } else {
            this._eventManager.emit('General.Message.CLOSE', this.getSlowConnectionMessage());
        }
    }

    protected onConnectionTimeout(): void {
        const popupParams: Message = {
            id: 'connectionTimeout',
            type: 'error',
            title: this._language.get('warning'),
            message: this._language.get('connectionTimeout')
        };

        this.emitCloseCurrentPopup();
        this.emitShowErrorPopup(popupParams);
    }

    protected getSlowConnectionMessage(): Message {
        return {
            id: PPErrorsHandler.SLOW_CONNECTION_POPUP_ID,
            type: 'error',
            title: this._language.get('warning'),
            message: this._language.get('slowConnection')
        };
    }

    protected onConnectionSlow(): void {
        this.emitCloseCurrentPopup();
        this.emitShowErrorPopup(this.getSlowConnectionMessage());
    }

    protected onConnectionError(listenerId: string, error: any): void {
        const isActionGoToHome = error.ErrType === CONNECTION_ERROR_CODE && UniqueErrorCodes.includes(error.UniqueErrorCode);
        const needDepositButton = error.ErrCode === ErrorTypes.DEPOSIT_CASE_ERROR && this._settings.get('cashierUrl');
        const errorMessage: Message = {
            id: 'connectionError',
            type: 'error',
            title: {id: 'error'},
            message: error.ErrMessage || {id: 'connectionError'},
            buttons: [{
                title: {id: error.ErrMessage ? 'ok' : 'restart'},
                action: isActionGoToHome ? 'go_to_home' : 'reload'
            }]
        };

        if (errorMessage.buttons && needDepositButton) {
            errorMessage.buttons.push({
                title: {id: 'deposit'},
                action: 'deposit'
            });
        }

        this.emitCloseCurrentPopup();
        this.emitShowErrorPopup(errorMessage);
    }

    protected onGameUnavailable(): void {
        const errorPopupParams: Message = {
            id: 'GameUnavailable',
            type: 'error',
            title: this._language.get('error'),
            message: this._language.get('error2'),
            withoutBorder: false
        };

        this.emitCloseCurrentPopup();
        this.emitShowErrorPopup(errorPopupParams);
    }

    protected onPlaceBetError(): void {
        const errorPopupParams: Message = {
            id: 'PlaceBetError',
            type: 'error',
            title: this._language.get('error'),
            message: this._language.get('betPlaceError'),
            buttons: [{
                title: this._language.get('ok'),
                action: 'reload'
            }]
        };

        this.emitCloseCurrentPopup();
        this.emitShowErrorPopup(errorPopupParams);
    }

    protected onTemporaryError(listenerId: string, errorMsg: ServerErrorMessage): void {
        const errorPopupParams: Message = {
            id: 'TemporaryError',
            type: 'error',
            title: this._language.get('error'),
            message: errorMsg.ErrMessage || this._language.get('temporaryError')
        };

        if (!this._skipFollowingErrors) {
            this.emitCloseCurrentPopup();
            this.emitShowErrorPopup(errorPopupParams);

            this._skipFollowingErrors = errorMsg.ErrMessage !== undefined;
        }
    }

    protected onGameServerError(listenerId: string, error: ServerErrorMessage): void {
        const messageTextID = ErrorTypes.LOCAL_ERROR_CODE_MESSAGE_MAP[error.ErrCode!] || error.ErrMessageID;
        const errorMessage: Message = {
            id: 'messageID',
            type: 'error',
            title: {id: 'error'},
            message: messageTextID ? {id: messageTextID} : error.ErrMessage!,
            buttons: [{
                title: this._language.get('ok'),
                action: 'reload'
            }]
        };

        this.emitShowErrorPopup(errorMessage);
    }

    protected getInvalidBalanceError(): Message {
        return {
            title: this._language.get('error'),
            message: this._language.get('temporaryError'),
            id: PPErrorsHandler.INVALID_BALANCE_POPUP_ID,
            type: 'error'
        };
    }

    protected onInvalidBalanceError(): void {
        this.emitCloseCurrentPopup();
        this.emitShowErrorPopup(this.getInvalidBalanceError());
    }

    protected onBalanceUpdated(): void {
        this._eventManager.emit('General.Message.CLOSE', this.getInvalidBalanceError());
    }

    protected emitCloseCurrentPopup(): void {
        this._skipFollowingErrors = false;
        this._eventManager.emit('General.Message.CLOSE');
    }

    protected emitShowErrorPopup(errorPopupParams: Message): void {
        this._eventManager.emit('General.Message.SHOW', errorPopupParams);
    }

    protected on(id: string | symbol, listener: Function, priority?: number): void {
        (this._eventManager as IEventManagerWithAsync).on(id, listener.bind(this, id), this, priority);
    }
}
